import React, { useEffect, useRef, useState } from 'react';
import './styles.scss';

type Pt = [number, number];

interface MarkerState {
  visible: boolean;
  o?: Pt;
}

const SEND_EVERY_MS = 33;
// Radius in pixels of the area that grabs the piece around its marker
const GRAB_RADIUS = 46;

const send = (payload: Record<string, unknown>): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify({ source: 'placement-gizmo', ...payload }));
  } catch (e) {
    // Running outside the game (e.g. Storybook)
  }
};

// Grabbing the piece being placed (admin tool): a marker follows it on the screen, and once the browser has
// the focus it is dragged with the mouse. The drag moves the piece on the ground, following the cursor;
// with Shift held it goes up and down; the wheel turns it. objectPlacementService.ts turns the screen
// movement into a world movement; the server still validates the final spot.
const PlacementGizmo: React.FC = () => {
  const [m, setM] = useState<MarkerState>({ visible: false });
  const [grabbed, setGrabbed] = useState(false);
  const [size, setSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  const last = useRef<{ x: number; y: number; sent: number; pendingX: number; pendingY: number } | null>(null);

  useEffect(() => {
    const onMarker = (e: Event) => setM((e as CustomEvent).detail as MarkerState);
    const onResize = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    // The piece is let go whenever the browser loses the mouse (F7 again, focus lost, window left): a release
    // that never reached the page must not leave it stuck to the cursor
    const release = () => {
      last.current = null;
      setGrabbed(false);
    };
    window.addEventListener('skymp5-client:placementGizmo', onMarker);
    window.addEventListener('skymp5-client:browserUnfocused', release);
    window.addEventListener('blur', release);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('skymp5-client:placementGizmo', onMarker);
      window.removeEventListener('skymp5-client:browserUnfocused', release);
      window.removeEventListener('blur', release);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  if (!m.visible || !m.o) return null;

  const x = m.o[0] * size.w;
  const y = m.o[1] * size.h;

  const flush = (shift: boolean) => {
    const l = last.current;
    if (!l) return;
    if (l.pendingX === 0 && l.pendingY === 0) return;
    send({ action: 'grab', dx: l.pendingX / size.w, dy: l.pendingY / size.h, vertical: shift });
    l.pendingX = 0;
    l.pendingY = 0;
    l.sent = Date.now();
  };

  const onDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    last.current = { x: e.clientX, y: e.clientY, sent: 0, pendingX: 0, pendingY: 0 };
    setGrabbed(true);
    e.preventDefault();
  };

  // The movement since the previous message is sent: the piece is read again at its new place each time
  const onMove = (e: React.PointerEvent) => {
    const l = last.current;
    if (!l) return;
    // No button held any more: the release was missed, the piece is let go here
    if ((e.buttons & 1) === 0) {
      last.current = null;
      setGrabbed(false);
      return;
    }
    l.pendingX += e.clientX - l.x;
    l.pendingY += e.clientY - l.y;
    l.x = e.clientX;
    l.y = e.clientY;
    if (Date.now() - l.sent >= SEND_EVERY_MS) flush(e.shiftKey);
  };

  const onUp = (e: React.PointerEvent) => {
    flush(e.shiftKey);
    last.current = null;
    setGrabbed(false);
  };

  const onWheel = (e: React.WheelEvent) => {
    if (e.deltaY === 0) return;
    // Ctrl + wheel changes the size, the wheel alone turns the piece
    send({ action: e.ctrlKey ? 'scale' : 'wheel', dir: e.deltaY < 0 ? 1 : -1 });
  };

  return (
    <svg className="placement-gizmo" width={size.w} height={size.h} viewBox={`0 0 ${size.w} ${size.h}`}>
      <g className={`placement-gizmo__marker${grabbed ? ' is-grabbed' : ''}`} transform={`translate(${x} ${y})`}>
        <circle r={16} className="placement-gizmo__marker-ring" />
        <circle r={3.5} className="placement-gizmo__marker-dot" />
        <path d="M -26 0 H -12 M 12 0 H 26 M 0 -26 V -12 M 0 12 V 26" className="placement-gizmo__marker-ticks" />
        <circle
          r={GRAB_RADIUS}
          className="placement-gizmo__grab"
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onLostPointerCapture={onUp}
          onWheel={onWheel}
        />
      </g>
    </svg>
  );
};

export default PlacementGizmo;
